import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash/debounce';

import MDTypography from 'components/MDTypography';
import { duplicateUsersColumns, nonUsUsersColumns, usUsersColumns, usUsersExportColumns } from 'constants/columns.constants';
import { downloadCSV, downloadXLSX } from 'helpers/fileDownload';
import { nonUsUserRows, usUserRows } from 'layouts/UserManagement/components/Rows';
import moment from 'moment';
import PapaParse from 'papaparse';
import { toast } from 'react-toastify';
import { downloadUserServices } from 'services/users.service';
import {
  freezeUserAction,
  getDuplicateUsersList,
  getNonUSUsersList,
  getUsersList,
  resetUsersList,
} from 'store/reducers/usersSlice';
import {
  formatCurrency,
  formatPhoneNumber,
  formatSSN,
} from '../helpers/formatData';

const useUsersHook = ({ usersFrom = 'us' }) => {
  const [isLoading, setLoading] = useState(false);
  const [isFreezeLoading, setIsFreezeLoading] = useState(false);

  const dispatch = useDispatch();

  const { usersList } = useSelector((state) => state.usersSlice);

  const [selectedUser, setSelectedUser] = useState(null);

  const [isFreezeModal, setFreezeModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const [userKYCStatus, setUserKYCStatus] = useState('');
  const [userInvestmentStatus, setUserInvestmentStatus] = useState('');
  const [regulationStatus, setRegulationStatus] = useState('');
  const [field, setField] = useState('');

  const handleUserKYCStatus = (event) => {
    setUserKYCStatus(event?.target?.value?.trim());
  };

  const handleUserInvestmentStatus = (event) => {
    setUserInvestmentStatus(event?.target?.value?.trim());
  };

  const handleRegulationStatus = (event) => {
    setRegulationStatus(event?.target?.value?.trim());
  }

  const handleDuplicateUserStatus = (event) =>{
    setField(event?.target?.value?.trim());
  }
  const columns = useMemo(() => {
    switch (usersFrom) {
      case 'us':
        return usUsersColumns;

      case 'non-us':
        return nonUsUsersColumns;
      
      case 'duplicate-users':
        return duplicateUsersColumns;

      default:
        return [];
    }
  }, [usersFrom]);

  const columnsExport = useMemo(() => {
    switch (usersFrom) {
      case 'us':
        return usUsersExportColumns;
      case 'non-us':
        return nonUsUsersColumns;
      default:
        return [];
    }
  }, [usersFrom])

  const renderUserFirstName = (row) => (
    <MDTypography
      fontSize="14px"
      color="text"
      sx={{
        maxWidth: '20ch',
      }}
    >
      {row?.name ?? 'NA'}
    </MDTypography>
  );

  const renderUserEmail = (row) => (
    <MDTypography
      fontSize="14px"
      color="text"
      sx={{
        maxWidth: '30ch',
      }}
    >
      {row?.email ?? 'NA'}
    </MDTypography>
  );

  const renderFreezeBtn = (row) => (
    <MDTypography
      fontSize="14px"
      color="text"
      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedUser(row);
        setFreezeModal(true);
      }}
    >
      {row?.isFreeze ? 'Unfreeze' : 'Freeze'}
    </MDTypography>)
  
  const renderSubscribeForSMS = (row) => {
    if(!row?.subscribeForSMS?.toString()?.length) return 'NA'
    return row?.subscribeForSMS === true ? 'Yes' : 'No'
  }

  const rows = useMemo(() => {
    switch (usersFrom) {
      case 'us':
      case 'duplicate-users':
        return usersList?.usersData?.length > 0
          ? usersList?.usersData?.map((user) => {
            return {
              id: user?.userId,
              firstName: renderUserFirstName(user),
              email: renderUserEmail(user),
              state: user?.state ?? 'NA',
              city: user?.city ?? 'NA',
              accountStatus: user?.accountStatus === 'Active' ? 'Yes' : 'No',
              isAdminApproved: user?.isAdminApproved === true ? 'Yes': 'No',
              investmentStatus: user?.investmentStatus ?? 'NA',
              regulationDStatus: user?.isRegDInvestor ?? 'NA',
              encodedSSN: formatSSN(user?.encodedSSN),
              phoneNumber: formatPhoneNumber(user?.phoneNumber) ?? 'NA',
              propertiesInvested: user?.totalProperties ?? 'NA',
              propertyValue: formatCurrency(user?.propertyValue ?? 0) ?? 'NA',
              referredBy: user?.referredBy ?? 'NA',
              referredByUser: user?.referredByUser?.name ?? 'NA',
              subscribeForSMS: renderSubscribeForSMS(user),
              onboardingStage: String(user?.onboardingStage) ?? 'NA',
              registeredOn: moment(user?.registeredOn ?? 'NA').format('MM/DD/YYYY'),
              isFreeze: renderFreezeBtn(user),
            };
          })
          : [];

      case 'non-us':
        return usersList?.usersData?.length > 0
          ? usersList?.usersData?.map((user) => {
            return {
              id: user?.userId,
              name: user?.name ?? 'NA',
              email: user?.email ?? 'NA',
              countryName: user?.country ?? 'NA',
              phoneNumber: formatPhoneNumber(user?.phoneNumber) ?? 'NA',
              referredBy: user.referredBy ?? 'NA',
              subscribeForSMS: renderSubscribeForSMS(user)
            };
          })
          : [];

      default:
        return [];
    }
  }, [usersFrom, usersList?.usersData]);

  async function fetchUsersList() {
    try {
      setLoading(true);
      switch (usersFrom) {
        case 'us':
          await dispatch(
            getUsersList({
              page: currentPage + 1,
              limit: perPage,
              sortField: sortBy?.id ?? 'id',
              sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
              userKYCStatus,
              userInvestmentStatus,
              regulationStatus,
              search: searchTerm,
            })
          );
          break;

        case 'non-us':
          await dispatch(
            getNonUSUsersList({
              page: currentPage + 1,
              limit: perPage,
              sortField: sortBy?.id ?? 'id',
              sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
              search: searchTerm,
            })
          );
          break;
        
        case 'duplicate-users':
          await dispatch(
            getDuplicateUsersList({
              page: currentPage + 1,
              limit: perPage,
              sortField: sortBy?.id ?? 'id',
              sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
              userKYCStatus,
              userInvestmentStatus,
              regulationStatus,
              search: searchTerm,
              field,
            })
          );
          break;
        default:
          break;
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onFreezeUserClick = async () => {
    try {
      setIsFreezeLoading(true)
      await dispatch(
        freezeUserAction({
          id: selectedUser?.userId,
          body: {
            isFreeze: !selectedUser?.isFreeze,
          },
        })
      );
    } catch (error) {
      console.log({ error });
    } finally {
      setIsFreezeLoading(false)
      onCloseModal();
    }
  };

  const getUsersName = () => {
    switch (usersFrom) {
      case 'us':
        return 'US';
      case 'non-us':
        return 'Non Us';
      default:
        return 'US'
    }
  }

  const getDownloadUsersRows = () => {
    switch (usersFrom) {
      case 'us':
        return usUserRows;
      case 'non-us':
        return nonUsUserRows;
      default:
        return usUserRows;
    }
  }

  const fetchDownloadUsers = async () => {
    try {
      const response = await downloadUserServices(
        getUsersName(),
        sortBy?.id ?? 'id',
        sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
        userKYCStatus,
        userInvestmentStatus,
        regulationStatus,
      );

      let users = [];

      switch (usersFrom) {
        case 'us':
          users = response.data?.data?.userList;
          break;
        case 'non-us':
          users = response.data?.data?.nonUSUserList;
          break;
        default:
          users = response.data?.data?.userList;
          break;
      }

      return users;
    } catch (error) {
      throw error;
    }
  }

  const onDownloadCSV = async () => {
    try {
      const users = await fetchDownloadUsers();
      const csv = PapaParse.unparse(
        users.map(getDownloadUsersRows()),
        {
          header: false,
        }
      );
      const headerParser = PapaParse.unparse(
        {
          fields: columnsExport.map((col, index) => {
            if (index === columnsExport.length - 1) {
              return 'isFreezed'; // Use 'Action' for the last column
            } else if (typeof col.Header === 'object' && col.Header.name) {
              return col.Header.name; // Use col.Header.name for other columns
            } else {
              return col.Header; // Use the default value for other columns
            }
          }),
          data: [],
        },
        {
          header: true,
        }
      );
      const formattedCSVData = headerParser + csv;

      if (!users?.length) {
        toast.warn(
          `Unable to download file as there are no records to export!`
        );
        return;
      }

      await downloadCSV(
        formattedCSVData,
        `${getUsersName()}-Users`
      );
    } catch (error) {
      toast.error(error?.message ?? 'Something went wrong');
    }
  };

  const onDownloadXLSX = async () => {
    try {
      const users = await fetchDownloadUsers();

      if (!users?.length) {
        toast.warn(`Unable to download file as there are no records to export!`);
        return;
      }

      const columnNames = columnsExport.map((col, index) => {
        if (index === columnsExport.length - 1) {
          return 'isFreezed'; // Use 'Action' for the last column
        } else if (typeof col.Header === 'object' && col.Header.name) {
          return col.Header.name; // Use col.Header.name for other columns
        } else {
          return col.Header; // Use the default value for other columns
        }
      });

      await downloadXLSX(
        users.map(getDownloadUsersRows()),
        columnNames,
        `${getUsersName()}-Users`
      );
    } catch (error) {
      toast.error(error?.message ?? 'Something went wrong');
    }
  };

  const onCloseModal = () => {
    setSelectedUser(false);
    setFreezeModal(false);
  };

  const onFetchDataDebounced = debounce(fetchUsersList, 0);

  useEffect(() => {
    setCurrentPage(0);
  }, [userKYCStatus]);

  useEffect(() => {
    setCurrentPage(0);
  }, [userInvestmentStatus]);

  useEffect(() => {
    setCurrentPage(0);
  }, [regulationStatus]);

  useEffect(()=>{
    setCurrentPage(0);
  },[field])

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [
    usersFrom,
    currentPage,
    sortBy,
    perPage,
    userKYCStatus,
    userInvestmentStatus,
    regulationStatus,
    searchTerm,
    field,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetUsersList());
    };
  }, []);

  return {
    usersList,
    rows,
    columns,
    usUsersExportColumns,
    totalCount: usersList?.totalCount,
    totalPage: usersList?.totalPage,
    perPage,
    setPerPage,
    currentPage,
    setCurrentPage,
    sortBy,
    setSortBy,
    isLoading,
    userKYCStatus,
    setUserKYCStatus,
    handleUserKYCStatus,
    userInvestmentStatus,
    setUserInvestmentStatus,
    handleUserInvestmentStatus,
    regulationStatus,
    setRegulationStatus,
    field,
    setField,
    handleRegulationStatus,
    handleDuplicateUserStatus,
    isFreezeModal,
    selectedUser,
    onFreezeUserClick,
    onCloseModal,
    searchTerm,
    setSearchTerm,
    onDownloadCSV,
    onDownloadXLSX,
    isFreezeLoading,
  };
};

export default useUsersHook;
